import { useState } from 'react';

import Button from '../../components/button/button';
import Card from '../../components/card/card';
import Input from '../../components/input/input';
import ResponseCodes from '../../constants/response-codes.constant';
import { useAuthStore } from '../../stores/auth-store';
import { usePendingPaymentStore } from '../../stores/pending-payment-store';
import { Analytics } from '../../utils/analytics.util';
import { mapResponseError } from '../../utils/error-mapper.util';
import { paymentsSDK } from '../../webservices/payments-api';

import styles from './confirm-direct-debit-otp.view.module.css';

type Props = {
    onBack: () => void;
    onRestart: () => void;
    onSuccess: () => void;
};

const ConfirmDirectDebitOTPView = ({ onBack, onRestart, onSuccess }: Props) => {
    const { paymentId, userId, clearPendingPayment } = usePendingPaymentStore();
    const { oauthToken } = useAuthStore();

    const [token, setToken] = useState('');
    const [loading, setLoading] = useState(false);
    const [rejectionError, setRejectionError] = useState('');

    const handleBack = () => {
        clearPendingPayment();
        onBack();
    };

    const handleRestart = () => {
        clearPendingPayment();
        onRestart();
    };

    const handleSubmit = async () => {
        if (!paymentId || !userId || !oauthToken || !token) return;
        setLoading(true);
        setRejectionError('');
        Analytics.track('OTPSubmitted', { action: 'ButtonPressed' });
        try {
            const result = await paymentsSDK.v2.payments.confirmDirectDebit({
                paymentId,
                userId,
                token,
                accessToken: oauthToken,
            });
            if (result.code !== ResponseCodes.PROCESS_OK) {
                const msg = mapResponseError(result);
                setRejectionError(msg);
                Analytics.track('OTPRejected', {
                    action: 'ButtonPressed',
                    error: msg,
                });
                return;
            }

            clearPendingPayment();
            Analytics.track('OTPApproved', { action: 'ButtonPressed' });
            onSuccess();
        } finally {
            setLoading(false);
        }
    };

    if (rejectionError) {
        return (
            <Card className={styles.card}>
                <h4>Pago rechazado</h4>
                <p data-testid="otp-error">{rejectionError}</p>
                <Button text="Reintentar pago" onClick={handleRestart} />
                <Button text="Volver" onClick={handleBack} />
            </Card>
        );
    }

    return (
        <Card className={styles.card}>
            <h4>Confirmación de débito directo</h4>
            <p>
                Tu banco enviará un código a tu teléfono. Ingrésalo aquí para
                confirmar el pago.
            </p>
            <Input
                placeholder="Código OTP"
                value={token}
                onInput={setToken}
                data-testid="otp-token-input"
            />
            <Button
                text={loading ? 'Confirmando...' : 'Confirmar pago'}
                disabled={loading || !token}
                onClick={handleSubmit}
            />
            <Button text="Volver" onClick={handleBack} />
        </Card>
    );
};

export default ConfirmDirectDebitOTPView;
